/**
 * KPI status computation for the Mini-Project Progress tab.
 *
 * Backend doesn't compute these (yet) — the Progress tab derives them
 * client-side from the sub-entity data. Logic per Mini-Project Framework
 * Spec sections 6.5 (Mission), 7.6 (Schedule), 14.2 (Summary table).
 *
 * Color codes match CLAUDE.md Management tab status palette:
 *   blue    — complete
 *   green   — on track
 *   yellow  — warning (behind but recoverable)
 *   red     — critical (significantly behind)
 *   grey    — N/A (draft / no data / no thresholds defined)
 *
 * Per Skip's 2026-05-19 decision: Mini-Project KPI cards show ONLY the
 * colored circle. No trend triangles (those are Full-Project-only —
 * deliberate upgrade signal).
 */

import type {
  MiniProject,
  MiniProjectTask,
  MiniProjectBomItem,
} from "../../api/miniProjects";
import type { MiniProjectGoalView } from "../../Data/MiniProjectConfig/miniProjectGoalView";
import type { MiniProjectStatus } from "../../api/miniProjectStatuses";
import {
  classifyScheduleGap,
  computeScheduleThresholds,
  projectDurationDays,
} from "../../Data/MiniProjectConfig/scheduleThresholds";
import { getActiveIndicatorPalette } from "../../theme/colorBlindMode";

export type KpiColor = "blue" | "green" | "yellow" | "red" | "grey";

export interface KpiStatus {
  summary: KpiColor;
  schedule: KpiColor;
  budget: KpiColor;
  mission: KpiColor;
  dataReliability: KpiColor;
}

/** Trend direction for a KPI from the previous status to the current one.
 *  'up' = improved (color got better), 'down' = worsened, 'flat' = same
 *  color, null = insufficient data (no previous status, or one side is
 *  grey/missing). Skip 2026-05-27 lock — see computeMpKpiTrends below. */
export type KpiTrend = "up" | "down" | "flat" | null;

export interface KpiTrends {
  summary: KpiTrend;
  schedule: KpiTrend;
  budget: KpiTrend;
  mission: KpiTrend;
  dataReliability: KpiTrend;
}

/** Numeric rank for KPI colors. Higher = better. Used by colorTrend()
 *  to decide direction between two colors. Grey is "no data" — treated
 *  as null/no comparison rather than ordinal-zero so a transition into
 *  or out of grey doesn't fake a trend. */
const COLOR_RANK: Record<Exclude<KpiColor, "grey">, number> = {
  red: 1,
  yellow: 2,
  green: 3,
  blue: 4, // "complete" — best
};

/** Compare two KPI colors, return trend. Skip 2026-05-27 — trend
 *  triggers ONLY on color change (no intra-color drift, no "1 pt
 *  closer to yellow" false signals). Grey on either side = null. */
function colorTrend(now: KpiColor, prev: KpiColor): KpiTrend {
  if (now === "grey" || prev === "grey") return null;
  const nowRank = COLOR_RANK[now as Exclude<KpiColor, "grey">];
  const prevRank = COLOR_RANK[prev as Exclude<KpiColor, "grey">];
  if (nowRank === prevRank) return "flat";
  return nowRank > prevRank ? "up" : "down";
}

/** Combine 2–3 component trends into the Summary trend. Rule (Skip
 *  2026-05-27): "any worsened → down; else any improved → up; else
 *  flat." A flat-only set stays flat; a null in the inputs is treated
 *  as flat for the rollup (a constituent we can't compare doesn't
 *  drive the summary either way). */
function rollupSummaryTrend(parts: KpiTrend[]): KpiTrend {
  const live = parts.filter((p) => p !== null);
  if (live.length === 0) return null;
  if (live.some((p) => p === "down")) return "down";
  if (live.some((p) => p === "up")) return "up";
  return "flat";
}

/** Map a budget assessment (migration 190) to a KPI color.
 *  under_budget + on_track → green; trending_over → yellow;
 *  over_budget → red; null → grey. */
function budgetAssessmentToColor(
  a: "under_budget" | "on_track" | "trending_over" | "over_budget" | null | undefined,
): KpiColor {
  if (a == null) return "grey";
  if (a === "under_budget" || a === "on_track") return "green";
  if (a === "trending_over") return "yellow";
  return "red"; // over_budget
}

/** Map a single mission qualitative assessment to a KPI color.
 *  Matches MiniProjectMissionTab's cell mapping. */
function missionAssessmentToColor(
  a: "ahead" | "on_track" | "behind" | "blocked" | "done",
): KpiColor {
  if (a === "blocked") return "red";
  if (a === "behind") return "yellow";
  if (a === "done") return "blue";
  return "green"; // ahead, on_track
}

/** Aggregate mission color from a full status's per-goal assessments.
 *  Worst-wins: any blocked → red; else any behind → yellow; else any
 *  in-progress (ahead/on_track) → green; else all done → blue; else
 *  grey (no assessments). Mirrors MissionModal.aggregateLatest's
 *  intent without pulling in the qualitativeLadder helper. */
function aggregateMissionColor(
  status: { goalAssessments: Array<{ assessment: string }> } | null,
): KpiColor {
  if (!status || status.goalAssessments.length === 0) return "grey";
  let hasBlocked = false;
  let hasBehind = false;
  let hasInProgress = false;
  let hasDone = false;
  for (const a of status.goalAssessments) {
    if (a.assessment === "blocked") hasBlocked = true;
    else if (a.assessment === "behind") hasBehind = true;
    else if (a.assessment === "ahead" || a.assessment === "on_track") hasInProgress = true;
    else if (a.assessment === "done") hasDone = true;
  }
  if (hasBlocked) return "red";
  if (hasBehind) return "yellow";
  if (hasInProgress) return "green";
  if (hasDone) return "blue";
  return "grey";
}

/** Compute trend indicators for the MP KPI shapes (2026-05-27, Skip
 *  unlocked once status snapshots existed). Compares the latest
 *  filed status to the previous one, color-vs-color. Caller passes
 *  in the data; this function is pure.
 *
 *  Trend rules (locked):
 *    - Window: previous status, full stop. No "last N days" gating.
 *    - Color change only: a yellow→yellow with internal drift is flat.
 *    - Up arrow = improved everywhere (budget growing 20% is worse,
 *      arrow goes DOWN — handled because budgetAssessmentToColor
 *      bakes the inversion in).
 *    - First status: all trends null → all circles. Same for any KPI
 *      where either side resolves to grey.
 *    - DR: always null. DR is computed live from staleness; trending
 *      it requires snapshot infrastructure that doesn't exist yet.
 */
export function computeMpKpiTrends(args: {
  latestSummary: { plannedPct: number; actualPct: number; budgetAssessment?: "under_budget" | "on_track" | "trending_over" | "over_budget" | null } | null;
  previousSummary: { plannedPct: number; actualPct: number; budgetAssessment?: "under_budget" | "on_track" | "trending_over" | "over_budget" | null } | null;
  latestFull: { goalAssessments: Array<{ assessment: string }> } | null;
  previousFull: { goalAssessments: Array<{ assessment: string }> } | null;
  durationDays: number | null;
}): KpiTrends {
  // No previous = no trend. First status case → everything circle.
  if (!args.latestSummary || !args.previousSummary) {
    return { summary: null, schedule: null, budget: null, mission: null, dataReliability: null };
  }

  const thresholds = computeScheduleThresholds(args.durationDays);

  // Schedule — classify each summary's gap → success/warning/danger
  // → map to our KpiColor. classifyScheduleGap already returns the
  // color name; we map success→green / warning→yellow / danger→red.
  const scheduleNow = scheduleColorFromSummary(args.latestSummary, thresholds);
  const schedulePrev = scheduleColorFromSummary(args.previousSummary, thresholds);
  const schedule = colorTrend(scheduleNow, schedulePrev);

  // Budget — from the precomputed assessment on each summary (migration 190).
  const budget = colorTrend(
    budgetAssessmentToColor(args.latestSummary.budgetAssessment),
    budgetAssessmentToColor(args.previousSummary.budgetAssessment),
  );

  // Mission — needs the goal assessment arrays from the full statuses.
  // When either full status isn't loaded yet, mission trend stays null.
  const mission = colorTrend(
    aggregateMissionColor(args.latestFull),
    aggregateMissionColor(args.previousFull),
  );

  // Summary — derived from the three above. Worst-wins direction.
  const summary = rollupSummaryTrend([schedule, mission, budget]);

  return { summary, schedule, budget, mission, dataReliability: null };
}

/** Map a status summary's planned/actual gap to a KPI color via the
 *  shared schedule-thresholds classifier. */
function scheduleColorFromSummary(
  s: { plannedPct: number; actualPct: number },
  thresholds: ReturnType<typeof computeScheduleThresholds>,
): KpiColor {
  const gap = classifyScheduleGap(
    Number(s.plannedPct),
    Number(s.actualPct),
    thresholds,
  );
  if (gap.color === "success") return "green";
  if (gap.color === "warning") return "yellow";
  return "red"; // danger
}

/** Days since the most recent update across all sub-entities, or null. */
function daysSinceLastUpdate(mp: MiniProject, lastDataUpdateAt: string | null): number | null {
  // Use the precomputed mp.lastDataUpdateAt if the backend trigger
  // populates it (DEV-900 left it nullable). Otherwise fall back to the
  // caller-supplied derived value.
  const ts = mp.lastDataUpdateAt ?? lastDataUpdateAt ?? mp.updatedAt;
  if (!ts) return null;
  const ms = Date.now() - new Date(ts).getTime();
  return ms / 86400000;
}

/** Percentage of time elapsed in the project window. 0–1+, null if no dates. */
function timeElapsedPct(mp: MiniProject): number | null {
  if (!mp.startDate || !mp.endDate) return null;
  const start = new Date(mp.startDate).getTime();
  const end = new Date(mp.endDate).getTime();
  if (end <= start) return null;
  const now = Date.now();
  return Math.max(0, (now - start) / (end - start));
}

/** Time-relative comparison helper. Used by Mission + Schedule per spec §6.5. */
function timeRelativeColor(attainmentPct: number, elapsedPct: number): KpiColor {
  if (elapsedPct === 0) return "green"; // project just started
  if (attainmentPct >= elapsedPct) return "green";
  if (attainmentPct >= elapsedPct * 0.7) return "yellow";
  return "red";
}

// DEV-1116 (2026-05-26): Schedule and Mission dots rewired to the new
// data sources that the corresponding modals already use:
//   - Schedule reads the most recent mini_project_status's planned/actual
//     and classifies via the same `classifyScheduleGap` helper the modal
//     calls. Dot and modal now agree by construction.
//   - Mission reads each goal's latestAssessment (derived in
//     miniProjectGoalView at the data boundary) and aggregates via the
//     5-state qualitative ladder — only counting goals with
//     includedInRollup=true (Skip's caveat).
// Budget remains hardcoded grey by Skip's 2026-05-26 decision (option c)
// until pilot data tells us what Leads actually want from the metric.
// The pre-DEV-1116 dot computation used tasks_done/total and
// currentValue/targetValue averages — both old data sources that
// silently disagreed with what the modals showed.
export function computeMiniProjectKpiStatus(
  mp: MiniProject,
  goals: MiniProjectGoalView[],
  _tasks: MiniProjectTask[],
  _bom: MiniProjectBomItem[],
  latestStatus: MiniProjectStatus | null,
): KpiStatus {
  // Lifecycle-driven short-circuits per spec §14.2.
  // Archive (post-2026-05-19 refactor) is a separate tag, NOT a lifecycle
  // state — a complete-and-archived project still shows blue circles.
  if (mp.status === "complete") {
    return { summary: "blue", schedule: "blue", budget: "blue", mission: "blue", dataReliability: "blue" };
  }
  if (mp.status === "draft" || mp.status === "expired") {
    return { summary: "grey", schedule: "grey", budget: "grey", mission: "grey", dataReliability: "grey" };
  }

  // Schedule — DEV-1116 rewire. Read planned/actual from the latest
  // mini_project_status row and classify via the shared scheduleThresholds
  // helper that the Schedule modal also uses. Dot + modal share the same
  // classifier → guaranteed agreement.
  let schedule: KpiColor;
  if (!latestStatus) {
    // No status filed yet → grey (no signal to color).
    schedule = "grey";
  } else {
    const durationDays = projectDurationDays(mp.startDate, mp.endDate);
    const thresholds = computeScheduleThresholds(durationDays);
    const gap = classifyScheduleGap(
      latestStatus.plannedPct,
      latestStatus.actualPct,
      thresholds,
    );
    schedule =
      gap.color === "success" ? "green"
      : gap.color === "warning" ? "yellow"
      : "red";
  }

  // Mission — DEV-1116 rewire. Aggregate per-goal qualitative
  // assessments via the Path A 5-state ladder. Only goals with
  // includedInRollup=true contribute (Skip's confirmed caveat — and
  // the same flag the kpi-engine respects).
  //
  // Aggregation rule (any-worst-wins by severity):
  //   - any "blocked"  → red
  //   - any "behind"   → yellow
  //   - otherwise (all on_track / ahead / done) → green
  //   - no assessments at all → grey (honest absence)
  let mission: KpiColor;
  const includedGoals = goals.filter((g) => g.includedInRollup);
  const assessments = includedGoals
    .map((g) => g.latestAssessment)
    .filter((a): a is string => a !== null && a !== undefined);
  if (assessments.length === 0) {
    mission = "grey";
  } else if (assessments.includes("blocked")) {
    mission = "red";
  } else if (assessments.includes("behind")) {
    mission = "yellow";
  } else {
    // all on_track / ahead / done — Lead-judged on or above track
    mission = "green";
  }

  // Budget — Migration 190 (Skip 2026-05-28). Replaces the previously
  // hardcoded grey with a color driven by the Lead's most recent
  // budget_assessment on the latest filed status. Four-state ladder:
  //
  //   under_budget   → green   (spending less than planned)
  //   on_track       → green   (within tolerance)
  //   trending_over  → yellow  (exceeding plan, recoverable)
  //   over_budget    → red     (significantly over, intervention needed)
  //   null / no status → grey   (no assessment yet, honest absence)
  //
  // StatusWriter offers an auto-suggested value computed from
  // sum(actual_total) ÷ planned_budget_amount, but the Lead's pick is
  // the source of truth — same pattern Mission uses (per-goal
  // assessment is the source of truth, not the value snapshot).
  let budget: KpiColor;
  const ba = latestStatus?.budgetAssessment;
  if (!ba) {
    budget = "grey";
  } else if (ba === "under_budget" || ba === "on_track") {
    budget = "green";
  } else if (ba === "trending_over") {
    budget = "yellow";
  } else if (ba === "over_budget") {
    budget = "red";
  } else {
    // Unknown enum value (shouldn't happen — controller validates) —
    // fall back to grey rather than miscoloring.
    budget = "grey";
  }

  // Data Reliability — staleness. Skip's 2026-05-19 "staleness only" call.
  // Thresholds: green ≤ 3 days, yellow 4–7, red > 7.
  let dataReliability: KpiColor;
  const daysSince = daysSinceLastUpdate(mp, mp.updatedAt);
  if (daysSince === null) dataReliability = "grey";
  else if (daysSince <= 3) dataReliability = "green";
  else if (daysSince <= 7) dataReliability = "yellow";
  else dataReliability = "red";

  // Summary — average of the colored ones (treat grey as N/A and skip).
  const others = [schedule, budget, mission, dataReliability].filter((c) => c !== "grey");
  const summary = summarizeColors(others);

  return { summary, schedule, budget, mission, dataReliability };
}

function summarizeColors(colors: KpiColor[]): KpiColor {
  if (colors.length === 0) return "grey";
  if (colors.includes("red")) return "red";
  if (colors.includes("yellow")) return "yellow";
  if (colors.every((c) => c === "green" || c === "blue")) return "green";
  return "grey";
}

/** Map KpiColor → hex from the platform's active indicator palette.
 *
 *  Delegates to getActiveIndicatorPalette() so MP renders the EXACT same
 *  colors as Full Project surfaces (KpiGridTable row indicators, FP KPI
 *  Performance tab, etc.) AND honors the color-blind-safe palette
 *  toggle. Previously this returned a hand-picked literal hex set that
 *  drifted from the FP palette, producing a visible color mismatch
 *  between MP detail and FP grid rendering for the same status. Skip
 *  flagged this 2026-05-27.
 */
export function kpiColorHex(color: KpiColor): string {
  const p = getActiveIndicatorPalette();
  switch (color) {
    case "blue":   return p.completed;
    case "green":  return p.green;
    case "yellow": return p.yellow;
    case "red":    return p.red;
    case "grey":
    default:       return p.grey;
  }
}
